import * as React from "react";
import Avatar from "@mui/joy/Avatar";
import Chip from "@mui/joy/Chip";
import IconButton from "@mui/joy/IconButton";
import Stack from "@mui/joy/Stack";
import Typography from "@mui/joy/Typography";
import CircleIcon from "@mui/icons-material/Circle";
import { toggleMessagesPane } from "../../utils/chat-helper";
import useScript from "../../utils/useScript";
import EditGroupModal from "./EditGroupModal";
import GroupedAvatars from "./GroupedAvatars";
import useSocketStore from "../../stores/store-socket";

/**
 * Props interface for the MessagesPaneHeader component.
 * 
 * @interface MessagesPaneHeaderProps
 * @property {boolean} typing - Indicates whether the other user is currently typing
 */
type MessagesPaneHeaderProps = {
  typing: boolean;
};

const useEnhancedEffect =
  typeof window !== "undefined" ? React.useLayoutEffect : React.useEffect;

/**
 * A component that displays the header of the messages pane in a chat interface.
 * Shows information about the current chat, including:
 * - User/group avatar
 * - Chat name
 * - Online status
 * - Typing indicator
 * - Edit group options (for group chats)
 * 
 * @component
 * @param {MessagesPaneHeaderProps} props - The props for the MessagesPaneHeader component
 * @returns {JSX.Element} A header component for the messages pane
 * 
 * @example
 * ```tsx
 * <MessagesPaneHeader typing={true} />
 * ```
 */
export default function MessagesPaneHeader({
  typing,
}: MessagesPaneHeaderProps) {
  const { selectedChat } = useSocketStore();
  const status = useScript(`https://unpkg.com/feather-icons`);

  useEnhancedEffect(() => {
    // Feather icon setup: https://github.com/feathericons/feather#4-replace
    // @ts-ignore
    if (typeof feather !== "undefined") {
      // @ts-ignore
      feather.replace();
    }
  }, [status]);
  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      sx={{
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
      py={{ xs: 2, md: 2 }}
      px={{ xs: 1, md: 2 }}
    >
      <Stack direction="row" spacing={{ xs: 1, md: 2 }} alignItems="center">
        <IconButton
          variant="plain"
          color="neutral"
          size="sm"
          sx={{
            display: { xs: "inline-flex", sm: "none" },
          }}
          onClick={() => toggleMessagesPane()}
        >
          <i data-feather="chevron-left" />
        </IconButton>
        {selectedChat && selectedChat.type == "dialog" && (
          <Avatar size="lg" src={selectedChat.sender.avatar} />
        )}
        {selectedChat && selectedChat.type == "group" && (
          <GroupedAvatars members={selectedChat.members ?? []} />
        )}
        <div>
          <Typography
            fontWeight="lg"
            fontSize="lg"
            noWrap
            endDecorator={
              selectedChat &&
              selectedChat.sender.online &&
              selectedChat.type == "dialog" ? (
                <Chip
                  variant="outlined"
                  size="sm"
                  color="neutral"
                  sx={{
                    "--Chip-radius": "6px",
                  }}
                  startDecorator={
                    <CircleIcon sx={{ fontSize: 8 }} color="success" />
                  }
                  slotProps={{ root: { component: "span" } }}
                >
                  {typing ? "Typing..." : "Online"}
                </Chip>
              ) : undefined
            }
          >
            {selectedChat?.sender.name}
          </Typography>
        </div>
      </Stack>
      <Stack spacing={1} direction="row" alignItems="center">
        {selectedChat && selectedChat.type == "group" && (
          <EditGroupModal
            id={selectedChat.sender.id}
            members={selectedChat.members}
            name={selectedChat.sender.username}
          ></EditGroupModal>
        )}
      </Stack>
    </Stack>
  );
}
